import { useEffect, useRef, useState } from "react";
import { CloseCircle, SearchNormal } from "iconsax-reactjs";
import { Input } from "../../../shared/ui";
import { useDebouncedValue } from "../../../hooks/use-debounced-value";
import { useCustomerSearch } from "../hooks/useCustomerSearch";
import { SelectedCustomer } from "../types";

type AudienceCustomerPickerProps = {
  value: SelectedCustomer[];
  onChange: (customers: SelectedCustomer[]) => void;
};

const AudienceCustomerPicker = ({ value, onChange }: AudienceCustomerPickerProps) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebouncedValue(query, 400);

  const { items, isLoading } = useCustomerSearch({
    q: debouncedQuery.trim() || undefined,
    limit: 8,
  });

  const selectedIds = new Set(value.map((customer) => customer.id));
  const results = items.filter((item) => !selectedIds.has(item.id));

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const removeCustomer = (id: string) => {
    onChange(value.filter((customer) => customer.id !== id));
  };

  const addCustomer = (customer: SelectedCustomer) => {
    onChange([...value, customer]);
    setQuery("");
    setIsOpen(false);
  };

  return (
    <div className="mt-2 space-y-2">
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {value.map((customer) => (
            <span
              key={customer.id}
              className="flex items-center gap-2 rounded-full bg-[#EEF1FB] py-1.5 pl-3 pr-2 text-[13px] font-[450] text-[#2C2F44]"
            >
              {customer.label}
              <button
                type="button"
                aria-label={`Remove ${customer.label}`}
                onClick={() => removeCustomer(customer.id)}
                className="text-[#6F739B] hover:text-[#2C2F44]"
              >
                <CloseCircle size={16} />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div ref={containerRef} className="relative">
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search by customer name, email, or business name"
          startIcon={<SearchNormal size={18} className="text-[#6F739B]" />}
          className="h-12 rounded-[14px]"
        />

        {isOpen && debouncedQuery.trim() ? (
          <div className="absolute left-0 top-full z-20 mt-2 max-h-[240px] w-full overflow-y-auto rounded-[14px] border border-[#E6E9F5] bg-white p-2 shadow-lg">
            {isLoading ? (
              <div className="px-3 py-4 text-[13px] text-[#6F739B]">
                Searching...
              </div>
            ) : results.length === 0 ? (
              <div className="px-3 py-4 text-[13px] text-[#6F739B]">
                No customers found.
              </div>
            ) : (
              results.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() =>
                    addCustomer({
                      id: item.id,
                      label: item.customerName,
                      email: item.customerEmail,
                    })
                  }
                  className="flex w-full flex-col items-start rounded-[10px] px-3 py-2 text-left transition-colors hover:bg-[#F7F8FD]"
                >
                  <span className="aku-body-small-medium text-[#2C2F44]">
                    {item.customerName}
                  </span>
                  <span className="truncate text-[12px] text-[#6F739B]">
                    {item.customerEmail} · {item.businessName}
                  </span>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AudienceCustomerPicker;
